/**
 * Trilingual UI strings for the shared `RequireAuth` gate
 * (legacy `frontend/`).
 *
 * `RequireAuth` redirects signed-out viewers to `/signin?dest=<url>`
 * or `/signup?dest=<url>` (Reddit-style). The only string needed on
 * the guarded page itself is the `aria-label` used while auth is
 * being hydrated on app boot (the brief window where we do not yet
 * know whether the viewer is signed in). The sign-in page owns its
 * own copy via `authUi.ts` and the shared `AuthModal`.
 *
 * Mirrors keys that should eventually live under an `AuthGate`
 * namespace in `frontend-next/messages/*.json` once the CRA
 * surfaces (settings, chat, tasks) migrate to the Next App Router.
 */
export type AuthGateLocale = "en-US" | "ja-JP" | "ko-KR"

interface AuthGateStrings {
    /**
     * Accessible name announced while the app is checking whether the
     * viewer is signed in. Screen readers hear this during the brief
     * `awaitingUser` window; after hydration the route either renders
     * the protected children or redirects to `/signin?dest=…`.
     */
    loadingAriaLabel: string
}

const STRINGS: Record<AuthGateLocale, AuthGateStrings> = {
    "en-US": {
        loadingAriaLabel: "Checking your session…",
    },
    "ja-JP": {
        loadingAriaLabel: "セッションを確認しています…",
    },
    "ko-KR": {
        loadingAriaLabel: "세션을 확인하는 중…",
    },
}

/**
 * Resolves a supported locale from the browser, mirroring the
 * fallback behavior used by the other `i18n/*Ui.ts` helpers.
 */
export function resolveAuthGateLocale(): AuthGateLocale {
    if (typeof navigator === "undefined" || !navigator.language) {
        return "en-US"
    }
    const lang = navigator.language.toLowerCase()
    if (lang.startsWith("ja")) {
        return "ja-JP"
    }
    if (lang.startsWith("ko")) {
        return "ko-KR"
    }
    return "en-US"
}

export function getAuthGateUiStrings(
    locale?: AuthGateLocale
): AuthGateStrings {
    const key = locale ?? resolveAuthGateLocale()
    return STRINGS[key] ?? STRINGS["en-US"]
}
